import * as React from 'react';
import { Enter, Space } from '@fluentui/keyboard-keys';
import {
  getIntrinsicElementProps,
  mergeCallbacks,
  slot,
  useFocusFinders,
  useId,
  useMergedRefs,
  useOverflowMenu,
} from '@fluentui/react-components';
import { useTimeout } from '@fluentui/react-utilities';
import { useReferenceListContext_unstable } from '../../contexts/referenceListContext';
import type { ReferenceOverflowButtonProps, ReferenceOverflowButtonState } from './ReferenceOverflowButton.types';
import { useDesignVersion } from '@fluentui-copilot/react-provider';

/**
 * Create the state required to render ReferenceOverflowButton.
 *
 * The returned state can be modified with hooks such as useReferenceOverflowButtonStyles_unstable,
 * before being passed to renderReferenceOverflowButton_unstable.
 *
 * @param props - props from this instance of ReferenceOverflowButton
 * @param ref - reference to root HTMLElement of ReferenceOverflowButton
 */
export const useReferenceOverflowButton_unstable = (
  props: ReferenceOverflowButtonProps,
  ref: React.Ref<HTMLButtonElement>,
): ReferenceOverflowButtonState => {
  'use no memo';

  const { children, id, text } = props;
  const designVersion = useDesignVersion(props.designVersion);

  const rootRef = React.useRef<HTMLButtonElement>(null);

  const [setTimeout] = useTimeout();

  const referenceOverflowButtonId = useId('reference-overflow-button-', id);

  const areReferencesExpanded = useReferenceListContext_unstable(ctx => ctx.areReferencesExpanded);
  const maxVisibleReferences = useReferenceListContext_unstable(ctx => ctx.maxVisibleReferences);
  const referenceListRef = useReferenceListContext_unstable(ctx => ctx.referenceListRef);
  const totalReferencesCount = useReferenceListContext_unstable(ctx => ctx.totalReferencesCount);
  const triggeredViaKeyboard = useReferenceListContext_unstable(ctx => ctx.overflowButtonTriggeredViaKeyboard);

  const { isOverflowing, overflowCount } = useOverflowMenu();

  const processedText = children ?? (typeof text === 'function' ? text(overflowCount) : text);

  const { findFirstFocusable, findNextFocusable } = useFocusFinders();
  const onKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLButtonElement>>(
    ev => {
      if (ev.key === Enter || ev.key === Space) {
        triggeredViaKeyboard.current = true;
      }
    },
    [triggeredViaKeyboard],
  );

  // We keep track of the previous overflow count to help determine the element to move focus to when expanding the references via keyboard.
  const previousOverflow = React.useRef(0);

  // When the references are expanded or collapsed via keyboard, we need to move focus to the appropriate element.
  React.useEffect(() => {
    // We only try to move focus if maxVisibleReferences is set and the overflow button was triggered via keyboard.
    if (maxVisibleReferences !== undefined && triggeredViaKeyboard.current) {
      let elementToFocus: HTMLElement | null | undefined;

      // If the references are collapsed, we move focus to the overflow button.
      if (!areReferencesExpanded) {
        elementToFocus = rootRef.current;
        previousOverflow.current = overflowCount;
      }
      // If the references are expanded, we move focus to the first reference that was hidden when they were collapsed.
      // We do this by moving focus according to the following heuristics:
      // - If maxVisibleReferences is what determines what overflows, focus is moved to the element that is in the maxVisibleReferences position of the ReferenceList's children.
      // - If the space available is what is determines what overflows, focus is moved to the element that is in the (totalReferencesCount - overflowCount) position of the ReferenceList's children.
      else if (rootRef.current && referenceListRef.current) {
        const positionToFocus =
          maxVisibleReferences < totalReferencesCount - previousOverflow.current
            ? maxVisibleReferences
            : totalReferencesCount - previousOverflow.current;

        let currentElement: HTMLElement | null | undefined = findFirstFocusable(referenceListRef.current);
        for (let i = 0; i < positionToFocus && currentElement; i++) {
          currentElement = findNextFocusable(currentElement);
        }

        elementToFocus = currentElement;
      }

      // We focus on the element and reset the triggeredViaKeyboard flag after a timeout to ensure that this is pushed at the end of the event queue.
      setTimeout(() => {
        elementToFocus?.focus();
        triggeredViaKeyboard.current = false;
      }, 0);
    }
  }, [
    areReferencesExpanded,
    findFirstFocusable,
    findNextFocusable,
    isOverflowing,
    maxVisibleReferences,
    overflowCount,
    referenceListRef,
    setTimeout,
    totalReferencesCount,
    triggeredViaKeyboard,
  ]);

  const state: ReferenceOverflowButtonState = {
    designVersion,
    id: referenceOverflowButtonId,
    shouldRenderOverflowButton: isOverflowing || areReferencesExpanded,

    components: {
      root: 'button',
    },

    root: slot.always(
      getIntrinsicElementProps('button', {
        ref: useMergedRefs(rootRef, ref),
        ...props,
      }),
      {
        defaultProps: {
          children: processedText,
        },
        elementType: 'button',
      },
    ),
  };

  state.root.onKeyDown = mergeCallbacks(onKeyDown, props.onKeyDown);

  return state;
};
